import { inject, Injectable } from "@angular/core";
import { LG_APP_CONFIGURATION, ServerDefinitionsBase } from "@logex/framework/lg-application";
import { StringKeyOf } from "@logex/framework/types";
import { Observable, ReplaySubject } from "rxjs";
import { first, map, tap } from "rxjs/operators";
import {
    DefinitionsAc1,
    DefinitionsAc2,
    DefinitionsAc3,
    DefinitionsActivity,
    DefinitionsAgeCategory,
    DefinitionsCareCluster,
    DefinitionsCcs0,
    DefinitionsComorbidityIndex,
    DefinitionsDiagnosis,
    DefinitionsDiagnosisGroup,
    DefinitionsFlag,
    DefinitionsGender,
    DefinitionsInsurer,
    DefinitionsLocation,
    DefinitionsMunicipality,
    DefinitionSocialStatus,
    DefinitionsReferral,
    DefinitionsReferrerType,
    DefinitionsRve,
    DefinitionsSpecialism,
    DefinitionsTrc1,
    DefinitionsTrc2,
    DefinitionsTrc3,
    DefinitionsUrgencyCare,
    IAppDefinitions
} from "./app-definitions.types";

import { Dictionary } from "lodash";

export type DefinitionKey = StringKeyOf<IAppDefinitions>;

// export type DefinitionDictionary<T extends DefinitionKey> = AppDefinitions[Extract<keyof IAppDefinitions, DefinitionKey>][T];
export type DefinitionDictionary = AppDefinitions[keyof IAppDefinitions];

export type DefinitionValues<T = DefinitionKey> = T extends "flag"
    ? DefinitionsFlag
    : T extends "bestPracticesFlag"
      ? DefinitionsFlag
      : T extends "ac1"
        ? DefinitionsAc1
        : T extends "ac2"
          ? DefinitionsAc2
          : T extends "ac3"
            ? DefinitionsAc3
            : T extends "activity"
              ? DefinitionsActivity
              : T extends "trc1"
                ? DefinitionsTrc1
                : T extends "trc2"
                  ? DefinitionsTrc2
                  : T extends "trc3"
                    ? DefinitionsTrc3
                    : T extends "diagnosisGroup"
                      ? DefinitionsDiagnosisGroup
                      : T extends "specialism"
                        ? DefinitionsSpecialism
                        : never;
// ----------------------------------------------------------------------------------
//
@Injectable({
    providedIn: "root"
})
export class AppDefinitions
    extends ServerDefinitionsBase<IAppDefinitions>
    implements IAppDefinitions
{
    private _loaded = new ReplaySubject<void>(1);

    // ----------------------------------------------------------------------------------
    // Fields
    ac1 = this.def<DefinitionsAc1>();
    ac2 = this.def<DefinitionsAc2>();
    ac3 = this.def<DefinitionsAc3>();
    activity = this.def<DefinitionsActivity>((code, item, displayMode) => {
        if (displayMode === "code") return item?.display_code ?? code;
    });

    ageCategory = this.def<DefinitionsAgeCategory>();
    careCluster = this.def<DefinitionsCareCluster>();
    ccs0 = this.def<DefinitionsCcs0>();
    diagnosisGroup = this.def<DefinitionsDiagnosisGroup>();
    diagnosis = this.def<DefinitionsDiagnosis>((code, item, displayMode) => {
        if (displayMode === "code") return item?.display_code;
    });

    gender = this.def<DefinitionsGender>();
    insurer = this.def<DefinitionsInsurer>();
    municipality = this.def<DefinitionsMunicipality>();

    referrerType = this.def<DefinitionsReferrerType>();
    socialStatus = this.def<DefinitionSocialStatus>();

    specialism = this.def<DefinitionsSpecialism>((_, item, displayMode) => {
        if (displayMode === "code") return item?.display_code;
    });

    trc1 = this.def<DefinitionsTrc1>(
        (_, item: DefinitionsTrc1) => item.display_code + ". " + item.name
    );

    trc2 = this.def<DefinitionsTrc2>(
        (_, item: DefinitionsTrc1) => item.display_code + ". " + item.name
    );

    trc3 = this.def<DefinitionsTrc3>();
    rve = this.def<DefinitionsRve>();
    comorbidityIndex = this.def<DefinitionsComorbidityIndex>();
    urgencyCare = this.def<DefinitionsUrgencyCare>();
    flag = this.def<DefinitionsFlag>((code, item, displayMode) => {
        if (displayMode === "custom") {
            return +code === 0 ? "" : item.name;
        }
    });

    bestPracticesFlag = this.def<DefinitionsFlag>();
    referral = this.def<DefinitionsReferral>();
    location = this.def<DefinitionsLocation>();

    // ----------------------------------------------------------------------------------
    //
    constructor() {
        super();

        const appConfiguration = inject(LG_APP_CONFIGURATION);

        this.init();
        this._setBaseUrl(appConfiguration.applicationRoot);
    }

    get loaded$(): Observable<void> {
        return this._loaded.pipe(first());
    }

    getDefinitionAsync<T = DefinitionKey>(defKey: T): Observable<Array<DefinitionValues<T>>> {
        return this._loaded.pipe(
            map(() => {
                const key = Object.keys(this).filter(x => x === defKey)[0];
                return Object.keys(this[key]).map(k => {
                    const pp: Dictionary<DefinitionValues<T>> = this[key];
                    return pp[k];
                });
            })
        );
    }

    load(...requiredDefinitions: DefinitionKey[]): Observable<void> {
        return super.load(...requiredDefinitions).pipe(tap(() => this._loaded.next()));
    }
}
